import { DashboardWidget, WidgetInstance } from "../DashboardWidgetConfig";
import { Layouts } from "react-grid-layout";
import { buildLayoutFromInstances } from "../../utils/generateLayouts";

export interface DashboardState {
  leftInstances: WidgetInstance[];
  rightInstances: WidgetInstance[];
  leftLayouts: Layouts;
  rightLayouts: Layouts;
  /**
   * Welcome-banner campaign version the user last dismissed.
   * Absent = never dismissed. See WELCOME_BANNER_CAMPAIGN.
   */
  welcomeBannerDismissedVersion?: number;
}

/**
 * Build a role-based default dashboard state.
 * Widgets are selected based on the user's otto_access.roles.
 * Resource/contract checks are already applied to widgetTypes — only widgets
 * available to the user are considered.
 */
export const getDefaultDashboardState = (
  roles: string[],
  widgetTypes: DashboardWidget[]
): DashboardState => {
  const hasRole = (...r: string[]) => r.some(role => roles.includes(role));
  const isGlobalOrRegional = hasRole('globalAdmin', 'globalUser', 'regionAdmin', 'regionUser');
  const isRegional = hasRole('regionAdmin', 'regionUser');
  const isClientAdmin = hasRole('clientAdmin');

  const hasWidget = (type: string) => widgetTypes.some(w => w.type === type);
  const hasOption = (type: string, value: string) =>
    widgetTypes.find(w => w.type === type)?.configOptions?.some(o => o.value === value) ?? false;

  // Left area — order determines layout priority:
  // assetMap first (top-left), IotAssetsStatus second (top-right per gap-fill logic)
  const leftDefs: Array<{ show: boolean; instance: WidgetInstance }> = [
    {
      show: hasWidget('assetMap'),
      instance: { instanceId: 'assetMap', type: 'assetMap' },
    },
    {
      show: hasWidget('IotAssetsStatus'),
      instance: { instanceId: 'IotAssetsStatus', type: 'IotAssetsStatus' },
    },
    {
      show: hasOption('kpi', 'totalAssetCount'),
      instance: { instanceId: 'default_kpi_totalAssetCount', type: 'kpi', config: { kpiType: 'totalAssetCount' } },
    },
    {
      show: hasOption('kpi', 'connectedPumps'),
      instance: { instanceId: 'default_kpi_connectedPumps', type: 'kpi', config: { kpiType: 'connectedPumps' } },
    },
    {
      show: (isGlobalOrRegional || isClientAdmin) && hasOption('kpi', 'totalUsers'),
      instance: { instanceId: 'default_kpi_totalUsers', type: 'kpi', config: { kpiType: 'totalUsers' } },
    },
    {
      show: isGlobalOrRegional && hasOption('kpi', 'totalCustomers'),
      instance: { instanceId: 'default_kpi_totalCustomers', type: 'kpi', config: { kpiType: 'totalCustomers' } },
    },
    {
      show: isGlobalOrRegional && hasOption('assetMetrics', 'harshCondition'),
      instance: { instanceId: 'default_assetMetrics_harshCondition', type: 'assetMetrics', config: { assetMetricType: 'harshCondition' } },
    },
    {
      show: isGlobalOrRegional && hasOption('assetMetrics', 'maintenanceDueSoon'),
      instance: { instanceId: 'default_assetMetrics_maintenanceDueSoon', type: 'assetMetrics', config: { assetMetricType: 'maintenanceDueSoon' } },
    },
    {
      show: isGlobalOrRegional && hasOption('assetMetrics', 'exhaustFilter'),
      instance: { instanceId: 'default_assetMetrics_exhaustFilter', type: 'assetMetrics', config: { assetMetricType: 'exhaustFilter' } },
    },
    {
      show: isGlobalOrRegional && hasOption('assetMetrics', 'missingSensor'),
      instance: { instanceId: 'default_assetMetrics_missingSensor', type: 'assetMetrics', config: { assetMetricType: 'missingSensor' } },
    },
    {
      show: isGlobalOrRegional && hasOption('assetMetrics', 'vsdCandidate'),
      instance: { instanceId: 'default_assetMetrics_vsdCandidate', type: 'assetMetrics', config: { assetMetricType: 'vsdCandidate' } },
    },
    {
      show: isGlobalOrRegional && hasOption('assetMetrics', 'sensorValueOutOfRange'),
      instance: { instanceId: 'default_assetMetrics_sensorValueOutOfRange', type: 'assetMetrics', config: { assetMetricType: 'sensorValueOutOfRange' } },
    },
  ];

  const rightDefs: Array<{ show: boolean; instance: WidgetInstance }> = [
    {
      show: hasWidget('assetShortcuts'),
      instance: { instanceId: 'assetShortcuts', type: 'assetShortcuts' },
    },
    {
      show: hasWidget('serviceUnit'),
      instance: { instanceId: 'serviceUnit', type: 'serviceUnit' },
    },
    {
      show: isRegional && hasOption('quickAction', 'removeAsset'),
      instance: { instanceId: 'default_quickAction_removeAsset', type: 'quickAction', config: { actionType: 'removeAsset' } },
    },
    {
      show: isRegional && hasOption('quickAction', 'installAsset'),
      instance: { instanceId: 'default_quickAction_installAsset', type: 'quickAction', config: { actionType: 'installAsset' } },
    },
    {
      show: isRegional && hasOption('quickAction', 'exchangeAsset'),
      instance: { instanceId: 'default_quickAction_exchangeAsset', type: 'quickAction', config: { actionType: 'exchangeAsset' } },
    },
  ];

  const leftInstances = leftDefs.filter(d => d.show).map(d => d.instance);
  const rightInstances = rightDefs.filter(d => d.show).map(d => d.instance);

  return {
    leftInstances,
    rightInstances,
    leftLayouts: buildLayoutFromInstances(leftInstances, widgetTypes, 'left'),
    rightLayouts: buildLayoutFromInstances(rightInstances, widgetTypes, 'right'),
  };
};

/**
 * @deprecated Use getDefaultDashboardState with widgetTypes instead.
 * Kept as a fallback for cases where widgetTypes are not yet available.
 */
export const DEFAULT_DASHBOARD_STATE: DashboardState = {
  leftInstances: [],
  rightInstances: [],
  leftLayouts: { lg: [], md: [], sm: [] },
  rightLayouts: { lg: [], md: [], sm: [] },
};
